import React from 'react';
import { Map } from 'immutable';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';

import { CallRecordKey, ContactKey, ExtensionKey } from 'flow/types/EntityKeys';
import { Contact } from 'flow/types/Contact';
import { TypedMap } from 'flow/types/TypedMap';
import SquareButtonsContainer from 'flow/components/contact-card/SquareButtonContainer';
import ContactCardTypeField from 'flow/components/contact-components/ContactCardTypeField';
import ProfileItem from 'flow/components/headers/ProfileItem';
import Loading from 'flow/components/ui-components/Loading';
import ContactAvatar from 'flow/components/ui-components/ContactAvatar';
import { handlePresenceNotification } from 'flow/actions/extension-actions';
import { ContactType } from 'flow/utils/extension-utils';
import { getStatusFromExtensionKey } from 'flow/selectors/selectors';
import { getContact } from 'flow/selectors/contact-selectors';
import { selectCallRecordContact } from 'flow/selectors/call-records-selectors';
import { getShouldNotifyPresence } from 'flow/selectors/extension-selectors';
import {
    selectExtensionActiveProfile,
    canSetPresenceNotification,
    selectOwnExtensionKey,
} from 'flow/selectors/ExtensionSelectors';

interface Props {
    contactKey: ContactKey;
    extensionKey: ExtensionKey;
    callRecordKey?: CallRecordKey;
    contact: TypedMap<Contact>;
}

const ContactHeader = ({
    contactKey,
    extensionKey,
    contact,
    callRecordKey,
}: Props) => {
    const dispatch = useAppDispatch();
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);

    const contactToUse = useAppSelector(
        (state) =>
            contact ||
            getContact(state, contactKey, extensionKey) ||
            selectCallRecordContact(state, callRecordKey),
    );

    const allowPresenceNotification = useAppSelector((state) =>
        canSetPresenceNotification(state, extensionKey),
    );
    const currentProfile = useAppSelector((state) =>
        selectExtensionActiveProfile(state, extensionKey),
    );
    const status = useAppSelector((state) =>
        getStatusFromExtensionKey(state, extensionKey),
    );
    const shouldNotifyPresence = useAppSelector((state) =>
        getShouldNotifyPresence(state, extensionKey),
    );

    if (!contactToUse) {
        return <Loading />;
    }
    const isBoundContact =
        contactToUse && contactToUse.get('type') === ContactType.bound.key;

    const emailAdress = contactToUse.get('email');
    const handleTogglePresenceNotification = extensionKey
        ? () =>
              dispatch(
                  handlePresenceNotification(
                      !shouldNotifyPresence,
                      ownExtensionKey,
                      Map()
                          .set('key', extensionKey)
                          .set('contact', contactToUse),
                  ),
              )
        : null;

    return (
        <div className="xs:flex-nowrap shadow-soft dark:shadow-base mt-3 flex grow flex-wrap items-center gap-6 rounded-sm bg-white p-6 dark:border-none dark:bg-gray-100">
            <div className="flex self-start">
                <ContactAvatar
                    contact={contactToUse}
                    status={status}
                    size="large"
                    canShowImagePreview={!!contactToUse?.get('avatarResource')}
                />
            </div>

            <div className="@container relative z-10 flex w-full flex-wrap items-center gap-3 lg:justify-end">
                {isBoundContact ? (
                    <ProfileItem
                        extensionKey={extensionKey}
                        contact={contactToUse}
                        activeProfile={currentProfile}
                    />
                ) : (
                    <ContactCardTypeField contact={contactToUse} />
                )}

                <SquareButtonsContainer
                    className="items-end self-end"
                    contact={contactToUse}
                    extensionKey={extensionKey}
                    emailAdress={emailAdress}
                    handleTogglePresenceNotification={
                        handleTogglePresenceNotification
                    }
                    shouldNotifyPresence={shouldNotifyPresence}
                    allowPresenceNotification={allowPresenceNotification}
                />
            </div>
        </div>
    );
};

export default ContactHeader;
